import { Loading } from '@ui-components/neo.tax';
import { useEffect, useMemo, useState } from 'react';

import { useAscContext } from '@neo.tax/asc/context';
import { useAscContributorProjectAllocations, useContributor } from '@neo.tax/asc/hooks';
import { AscAllocationProjectDetails } from '@neo.tax/asc/types';

import { StyledModal } from './contributor-allocation-modal.styled';
import { BaseAllocationModalLoaderProps, EditContributorAllocationModal } from './EditContributorAllocationModal';

interface EditContributorAllocationModalLoaderProps extends Omit<BaseAllocationModalLoaderProps, 'ascProjects'> {
  contributorId: string;
}

export const EditContributorAllocationModalLoader = ({
  contributorId,
  ...otherProps
}: EditContributorAllocationModalLoaderProps): React.ReactElement | null => {
  const skip = !otherProps.isOpen;
  const [searchKeyword, setSearchKeyword] = useState<string | null>(null);

  // Reset search keyword when modal closes
  useEffect(() => {
    if (!otherProps.isOpen) {
      setSearchKeyword(null);
    }
  }, [otherProps.isOpen]);

  const { fiscalYearId, periodName } = useAscContext();
  const { contributor, loading: loadingContributor } = useContributor(contributorId, { skip });

  // Fetch all project allocations (without search filter) for allocation calculations
  const {
    ascContributorProjectAllocations: allAscContributorProjectAllocations,
    loading: loadingAllAscContributorProjectAllocations,
  } = useAscContributorProjectAllocations({
    contributorId,
    fiscalYearId,
    periodName,
    skip,
  });

  // Fetch filtered project allocations for UI display
  const {
    ascContributorProjectAllocations: filteredAscContributorProjectAllocations,
    loading: loadingFilteredAscContributorProjectAllocations,
  } = useAscContributorProjectAllocations({
    contributorId,
    fiscalYearId,
    periodName,
    searchKeyword,
    skip,
  });

  const ascProjects: AscAllocationProjectDetails[] = useMemo(() => {
    if (!allAscContributorProjectAllocations) {
      return [];
    }

    const ascProjectSet = new Set<string>();
    const ascProjects: AscAllocationProjectDetails[] = [];
    for (const allocation of allAscContributorProjectAllocations) {
      if (ascProjectSet.has(allocation.ascProjectId)) {
        continue;
      }
      ascProjectSet.add(allocation.ascProjectId);
      ascProjects.push({
        id: allocation.ascProjectId,
        name: allocation.ascProjectName,
        capitalizationStatus: allocation.capitalizationStatus,
        capitalizationStartDate: allocation.capitalizationStartDate,
        capitalizationEndDate: allocation.capitalizationEndDate,
        clusterType: allocation.clusterType,
      });
    }
    return ascProjects;
  }, [allAscContributorProjectAllocations]);

  if (loadingContributor || !contributor) {
    return (
      <StyledModal isOpen={otherProps.isOpen} setIsOpen={otherProps.close}>
        <Loading height={120} />
      </StyledModal>
    );
  }

  return (
    <EditContributorAllocationModal
      contributor={contributor}
      ascProjects={ascProjects}
      allAscContributorProjectAllocations={allAscContributorProjectAllocations}
      allAscContributorProjectAllocationsLoading={loadingAllAscContributorProjectAllocations}
      filteredAscContributorProjectAllocations={filteredAscContributorProjectAllocations}
      filteredAscContributorProjectAllocationsLoading={loadingFilteredAscContributorProjectAllocations}
      searchKeyword={searchKeyword}
      setSearchKeyword={setSearchKeyword}
      {...otherProps}
    />
  );
};
